import HealthMonitor from '@omni/kit/HealthMonitor';
import { KitLoader } from '@omni/kit/components';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import KitNotFound from '@omni/kit/components/KitNotFound';
import KitTab from '@omni/kit/components/KitTab';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import GroupsService from '@omni/kit/services/GroupsService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import { useIsFocused } from '@react-navigation/native';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';

import { useChannelGroup } from '../../../hooks/useChannelGroup';
import { useTransformGroupEvents } from '../../../hooks/useTransformGroupEvents';
import { channelSelector } from '../../redux/selectors';
import CreateEditGroupEventScreen from './CreateEditGroupEventScreen';
import GroupAllListEvents from './components/GroupAllListEvents';

interface Props {
  navigation: any;
  route: any;
}
export default function GroupEventListScreen({
  route,
  navigation,
}: Props): ReactElement {
  const { tokens, user, app } = useShellContext();
  const appKey = app.appKey;
  const accessToken = useMemo(() => tokens.user || null, [tokens.user]);
  const { sizeClass } = useSizeClass();
  const userToken = accessToken?.replace('Bearer ', '');
  const channel = useSelector(channelSelector) as GroupChannel;
  const { t } = useTranslation(['groups']);
  let focused: boolean;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    focused = useIsFocused();
  } catch {
    focused = true; // Default to focused for web environments without navigation context
  }
  const group = useChannelGroup(channel?.url);
  const isResponseError = route.params.responseError;
  const [groupEventList, setGroupEventList] = useState<any[] | undefined>();
  const [isLoadingGroupEventList, setIsLoadingGroupEventList] =
    useState<boolean>(true);
  const [refresh, setRefresh] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const { upcomingEvents, pastEvents } = useTransformGroupEvents(
    groupEventList ? groupEventList : []
  );

  const fetchGroupEventList = useCallback(
    async (nextPage: number = 1, isUpcoming: boolean = true) => {
      if (!group?._embedded?.calendar?.id || !accessToken) return;

      const isFirstPage = nextPage === 1;

      try {
        if (isFirstPage) {
          setIsLoadingGroupEventList(true);
        } else {
          setIsPaginating(true);
        }

        const response = await GroupsService.GetGroupEventList({
          token: accessToken,
          filters: [
            ['app_key', appKey],
            ['calendar.id', group._embedded.calendar.id],
            ['status', 'published'],
            [
              'start_at',
              `${isUpcoming ? '>' : '<'}${new Date()
                .toISOString()
                .replace(/\.\d{3}Z$/, 'Z')}`,
            ],
          ],
          pages: [
            ['size', 20],
            ['number', nextPage],
          ],
          includes: ['sessions,sessions.counts'],
          sort: [isUpcoming ? 'start_at' : '-start_at'],
        });

        if (response.body) {
          const newEvents = response.body?._embedded?.events ?? [];

          setGroupEventList((prev) =>
            nextPage === 1 ? newEvents : [...(prev ?? []), ...newEvents]
          );

          setHasMore(newEvents.length === 20);
          setPage(nextPage);
        } else if (
          response.status &&
          !excludedStatusCodes.includes(response.status)
        ) {
          HealthMonitor.logError(response.message);
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        if (isFirstPage) {
          setIsLoadingGroupEventList(false);
        } else {
          setIsPaginating(false);
        }
      }
    },
    [accessToken, appKey, group?._embedded?.calendar?.id]
  );

  const [openEditGroupEventModal, setIsOpenEditGroupEventModal] =
    useState<boolean>(false);

  const openCreateEvent = () => {
    handleOnCreateGroupEvent();
  };
  const isSmall = sizeClass === SizeClass.Small;

  const handleOnCreateGroupEvent = () => {
    if (Platform.OS === 'web') {
      setIsOpenEditGroupEventModal(true);
    } else {
      navigation.navigate('create_event');
    }
  };
  const onRefreshSectionList = () => {
    setRefresh(true);
  };

  // Hooks
  useEffect(() => {
    navigation.setParams({ openCreateEvent });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  // Fetch events when switching tabs or on initial load
  useEffect(() => {
    if (focused) {
      const isUpcoming = activeTab === 0;
      setPage(1);
      setHasMore(true);
      setGroupEventList(undefined); // Clear existing data
      fetchGroupEventList(1, isUpcoming);
    }
  }, [activeTab, focused, fetchGroupEventList]);

  // Handle refresh
  useEffect(() => {
    if (refresh) {
      const isUpcoming = activeTab === 0;
      setPage(1);
      setHasMore(true);
      setGroupEventList(undefined);
      fetchGroupEventList(1, isUpcoming);
      setRefresh(false);
    }
  }, [refresh, fetchGroupEventList, activeTab]);

  return (
    <>
      <View
        style={[
          {
            backgroundColor: colorForScheme({
              default: Colors.N0,
            }),
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            paddingHorizontal:
              Platform.OS === 'web' && !isSmall ? Spacing.xxxxl : 0,
            paddingBottom: Spacing.m,
          },
        ]}
      >
        <KitTab
          onPress={() => setActiveTab(0)}
          active={activeTab === 0}
          fontSize={14}
        >
          {t('groups:upcomingEventsTab')}
        </KitTab>
        <KitTab
          onPress={() => setActiveTab(1)}
          active={activeTab === 1}
          fontSize={14}
        >
          {t('groups:pastEventsTab')}
        </KitTab>
      </View>
      {isLoadingGroupEventList ? (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
          }}
        >
          <KitLoader />
        </View>
      ) : isResponseError ? (
        <KitNotFound />
      ) : (
        <View
          style={[
            styles.container,
            {
              backgroundColor: colorForScheme({
                default: Colors.N0,
              }),
            },
          ]}
        >
          <GroupAllListEvents
            sections={activeTab === 0 ? upcomingEvents : pastEvents}
            upcoming={activeTab === 0}
            onRefresh={onRefreshSectionList}
            isRefreshing={isLoadingGroupEventList}
            userToken={userToken}
            onPressCreateGroupEvent={handleOnCreateGroupEvent}
            isPaginating={isPaginating}
            onEndReached={() => {
              if (!isPaginating && !isLoadingGroupEventList && hasMore) {
                fetchGroupEventList(page + 1, activeTab === 0);
              }
            }}
          />
        </View>
      )}
      <KitModalV2
        modalStyle={{
          width: '100%',
          maxWidth: 620,
          height: '100%',
          maxHeight: 700,
          padding: 0,
        }}
        coverScreen={true}
        anchorBottom={isSmall}
        isVisible={openEditGroupEventModal}
        onClose={() => setIsOpenEditGroupEventModal(false)}
        enableScroll
        noScrollView
      >
        <CreateEditGroupEventScreen
          onCancel={() => setIsOpenEditGroupEventModal(false)}
          onSuccess={() => setRefresh(true)}
        />
      </KitModalV2>
    </>
  );
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    alignSelf: 'center',
  },
});
